type TranslateFn = (key: string) => string;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function parseDateValue(dateValue: string | Date): Date {
  if (dateValue instanceof Date) {
    return new Date(dateValue.getTime());
  }
  if (typeof dateValue === 'string' && DATE_ONLY_PATTERN.test(dateValue)) {
    return new Date(`${dateValue}T00:00:00`);
  }
  return new Date(dateValue);
}

function normalizeLocale(rawLocale: unknown): string {
  if (typeof rawLocale === 'string') {
    const trimmed = rawLocale.trim();
    if (trimmed) {
      return trimmed.replace('_', '-');
    }
  }
  return 'es-ES';
}

function sanitizeLabel(value: string): string {
  return value.replace(/\.+$/, '');
}

function formatPart(date: Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

export function applyRelativeWeekdayLabel(
  date: Date,
  weekdayText: string,
  translate?: TranslateFn | null,
): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) {
    return typeof translate === 'function' ? translate('Today label') : 'Today';
  }
  if (diffDays === 1) {
    return typeof translate === 'function' ? translate('Tomorrow label') : 'Tomorrow';
  }
  return weekdayText;
}

export function formatScheduleDateParts(
  dateValue: string | Date,
  rawLocale?: string,
  translate?: TranslateFn | null,
): { month: string; day: string; weekday: string } {
  const date = parseDateValue(dateValue);
  if (Number.isNaN(date.getTime())) {
    return { month: '', day: '', weekday: '' };
  }

  const locale = normalizeLocale(rawLocale);
  const monthText = sanitizeLabel(formatPart(date, locale, { month: 'short' }));
  const dayText = formatPart(date, locale, { day: 'numeric' });
  const weekdayBase = sanitizeLabel(formatPart(date, locale, { weekday: 'short' }));
  const weekdayText = applyRelativeWeekdayLabel(date, weekdayBase, translate);

  return {
    month: monthText,
    day: dayText,
    weekday: weekdayText,
  };
}

export function formatShortMonthDay(dateValue: string | Date, rawLocale?: string): string {
  const date = parseDateValue(dateValue);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = normalizeLocale(rawLocale);
  const label = formatPart(date, locale, { day: '2-digit', month: 'short' });
  return sanitizeLabel(label);
}

export function formatShortTime(dateValue: string | Date, rawLocale?: string): string {
  const date = parseDateValue(dateValue);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = normalizeLocale(rawLocale);
  return sanitizeLabel(formatPart(date, locale, { hour: '2-digit', minute: '2-digit' }));
}
